import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { causasAPI } from '../services/api';

const Causas = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCausa, setEditingCausa] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [causaToDelete, setCausaToDelete] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    IdCausa: '',
    Causa: '',
    Carga: 0,
    Descarga: 0,
    Foto: 0,
    Observacion: 0,
  });

  // Query para obtener causas
  const {
    data: causasData,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ['causas'],
    queryFn: async () => {
      const response = await causasAPI.listar();
      return response.data;
    },
  });

  // Mutation para crear causa
  const createMutation = useMutation({
    mutationFn: causasAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['causas'] });
      handleCloseDialog();
      setError(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al crear la causa');
    },
  });

  // Mutation para actualizar causa
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => causasAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['causas'] });
      handleCloseDialog();
      setError(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al actualizar la causa');
    },
  });

  // Mutation para eliminar causa
  const deleteMutation = useMutation({
    mutationFn: causasAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['causas'] });
      setDeleteDialogOpen(false);
      setCausaToDelete(null);
      setDeleteInfo(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al eliminar la causa');
    },
  });

  const handleOpenDialog = (causa = null) => {
    if (causa) {
      setEditingCausa(causa);
      setFormData({
        IdCausa: causa.IdCausa,
        Causa: causa.Causa,
        Carga: causa.Carga,
        Descarga: causa.Descarga,
        Foto: causa.Foto,
        Observacion: causa.Observacion,
      });
    } else {
      setEditingCausa(null);
      setFormData({
        IdCausa: '',
        Causa: '',
        Carga: 0,
        Descarga: 0,
        Foto: 0,
        Observacion: 0,
      });
    }
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCausa(null);
    setError(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({ ...prev, [field]: checked ? 1 : 0 }));
  };

  const validateForm = () => {
    if (!formData.IdCausa || String(formData.IdCausa).trim() === '') {
      setError('El ID de causa es obligatorio');
      return false;
    }
    if (!formData.Causa || formData.Causa.trim() === '') {
      setError('La descripción de la causa es obligatoria');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const dataToSend = {
      IdCausa: parseInt(formData.IdCausa),
      Causa: formData.Causa.trim(),
      Carga: formData.Carga,
      Descarga: formData.Descarga,
      Foto: formData.Foto,
      Observacion: formData.Observacion,
    };

    if (editingCausa) {
      updateMutation.mutate({ id: editingCausa.Id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleDeleteClick = async (causa) => {
    setCausaToDelete(causa);
    try {
      const response = await causasAPI.verificarEliminacion(causa.Id);
      setDeleteInfo(response.data.data);
      setDeleteDialogOpen(true);
    } catch (error) {
      setError(error.response?.data?.error?.message || 'Error al verificar la causa');
    }
  };

  const handleConfirmDelete = () => {
    if (causaToDelete) {
      deleteMutation.mutate(causaToDelete.Id);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCausaToDelete(null);
    setDeleteInfo(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error al cargar las causas: {queryError?.message || 'Error desconocido'}
        </Alert>
      </Box>
    );
  }

  const causas = causasData?.data || [];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Causas / Observaciones
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Causa
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID Causa</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Carga</TableCell>
              <TableCell align="center">Descarga</TableCell>
              <TableCell align="center">Foto</TableCell>
              <TableCell align="center">Observación</TableCell>
              <TableCell align="center">Recolecciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {causas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No hay causas registradas
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              causas.map((causa) => (
                <TableRow key={causa.Id} hover>
                  <TableCell>{causa.IdCausa}</TableCell>
                  <TableCell>{causa.Causa}</TableCell>
                  <TableCell align="center">
                    {causa.Carga === 1 ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {causa.Descarga === 1 ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {causa.Foto === 1 ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {causa.Observacion === 1 ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">{causa.Qty_items || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(causa)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(causa)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar causa */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCausa ? 'Editar Causa' : 'Nueva Causa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="ID Causa"
              type="number"
              value={formData.IdCausa}
              onChange={(e) => handleChange('IdCausa', e.target.value)}
              disabled={!!editingCausa}
              required
              fullWidth
              helperText={editingCausa ? 'El ID no puede modificarse' : ''}
            />

            <TextField
              label="Descripción"
              value={formData.Causa}
              onChange={(e) => handleChange('Causa', e.target.value)}
              required
              fullWidth
              multiline
              rows={3}
            />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Configuración de la Causa
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Carga === 1}
                    onChange={(e) => handleCheckboxChange('Carga', e.target.checked)}
                  />
                }
                label="Carga"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Descarga === 1}
                    onChange={(e) => handleCheckboxChange('Descarga', e.target.checked)}
                  />
                }
                label="Descarga"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Foto === 1}
                    onChange={(e) => handleCheckboxChange('Foto', e.target.checked)}
                  />
                }
                label="Foto"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Observacion === 1}
                    onChange={(e) => handleCheckboxChange('Observacion', e.target.checked)}
                  />
                }
                label="Observación"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingCausa ? (
              'Actualizar'
            ) : (
              'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          {deleteInfo && (
            <>
              {deleteInfo.canDelete ? (
                <Alert severity="info">{deleteInfo.message}</Alert>
              ) : (
                <Alert severity="warning">{deleteInfo.message}</Alert>
              )}
              {causaToDelete && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>ID Causa:</strong> {causaToDelete.IdCausa}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Descripción:</strong> {causaToDelete.Causa}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={!deleteInfo?.canDelete || deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Causas;
